// Copyright (c) 2025, Keyfactor, Inc
'use strict'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

export const useDefaultGetQuery = ({ url, queryStringParams = {}, useQueryParams = {}, requestParams = {} }) => {
    const queryClient = useQueryClient()
    const queryKey = buildQueryKey(url, queryStringParams)

    return useQuery({
        queryKey,
        queryFn: async ({ signal }) => {
            // If refetchInterval is set, we need to let the backend know to not reset the session expiration
            let doNotResetSessionTimeout = false
            if (typeof useQueryParams.refetchInterval === 'function') {
                const queryCache = queryClient.getQueryCache()
                const query = queryCache.find({ queryKey })
                doNotResetSessionTimeout = useQueryParams.refetchInterval(query) > 0
            } else {
                doNotResetSessionTimeout = useQueryParams.refetchInterval > 0
            }

            return (
                await axios.get(url, {
                    params: queryStringParams,
                    signal,
                    doNotResetSessionTimeout,
                    noNetworkFailure: doNotResetSessionTimeout, // Prevent network error modal for polling requests
                    ...requestParams,
                })
            ).data
        },
        refetchOnWindowFocus: false,
        retry: false,
        staleTime: 0,
        ...useQueryParams,
    })
}

export const buildQueryKey = (url, queryStringParams = {}) => {
    const queryKey = [url]
    if (Object.keys(queryStringParams).length > 0) {
        queryKey.push(queryStringParams)
    }
    return queryKey
}

export const useDefaultPostMutation = ({ url, useMutationParams = {} }) =>
    useMutation({
        mutationFn: async params => await axios.post(url, params),
        ...useMutationParams,
    })

export const useDefaultPutMutation = ({ url, useMutationParams = {} }) =>
    useMutation({
        mutationFn: async params => await axios.put(url, params),
        ...useMutationParams,
    })
